<script setup>
import { ref, computed, watch } from "vue";
import { router, useForm, usePage } from "@inertiajs/vue2";
import axios from "axios";
import TipTapReader from "../Editor/TipTapReader.vue";
import { closeModal } from "../../Composables/useCloseModal";

// Props

const props = defineProps({
    crmClient: {
        type: Object,
        required: true,
    },
    relationships: {
        type: Array,
        default: () => [],
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
});

// Emits

const emit = defineEmits(["reset", "enroll", "toast"]);

// Reactive State

const dialog = ref(null);
const uniqueCampaignsbyNameAndCategory = ref([]);
const templateCampaigns = ref([]);
const campaignsLoading = ref(false);
const campaignLoading = ref(false);

const campaign = ref(null);
const programs = ref([]);
const messages = ref([]);
const instruments = ref([]);

const form = useForm({
    student: props.crmClient,
    campaign: null,
    campaignable_id: props.crmClient.id,
    campaignable_type: props.crmClient.type,
    location_id: usePage().props.auth.user.selected_location.id,
    program_id: null,
    instrument_id: null,
    day_offset: null,
});

// Methods

function fetchCampaigns() {
    campaignsLoading.value = true;
    axios.get(route("campaigns.index", { unique: 1 })).then((resp) => {
        uniqueCampaignsbyNameAndCategory.value = resp.data;
        uniqueCampaignsbyNameAndCategory.value.forEach((campaign) => {
            campaign.group = `${campaign.name}-${campaign.category}`;
        });
        campaignsLoading.value = false;
        //newCampaign.value = true;
    });
}

/**
 * Retreive all campaigns that share the same name and category
 * Set program and instrument options from results
 */
function handleCampaignSelect(campaign) {
    if (!campaign) return;
    campaignLoading.value = true;

    axios
        .get(route("campaignSubscriptions.create", campaign.id))
        .then((resp) => {
            const data = resp.data;

            //campaign.value = data.campaign;
            //messages.value = data.campaign.messages;
            templateCampaigns.value = data.campaigns;
            templateCampaigns.value.forEach((e) => {
                e.group = `${e.name}-${e.category}`;
            });
            programs.value = data.programs;
            instruments.value = data.instruments;

            if (programs.value.length == 1) {
                form.program_id = programs.value[0].id;
                setCampaignFromProgram(form.program_id);
            }

            form.instrument_id = form.student.instrument_id ?? null;
            campaignLoading.value = false;

            // I need to just set form.campaign_id if this returns a campaign (meaning no program)
        });
}

/**
 * Set the campaign id from the program id
 * @param {number} programId
 * @returns {void}
 * Campaigns are unique by name and category, so we don't actually know the id until we select a program
 */
function setCampaignFromProgram(programId) {
    const getCampaign = templateCampaigns.value.find(
        (e) => e.program?.id == programId || !e.program
    );

    if (!getCampaign) {
        form.campaign = null;
        form.program_id = null;
        form.instrument_id = null;
        messages.value = [];
        instruments.value = [];
        return;
    }

    form.campaign = getCampaign;
    messages.value = getCampaign.messages;
    form.day_offset = messages.value[0]?.day_offset ?? null;
}

function reset(bubble = false) {
    if (bubble) emit("reset");
    form.campaign = null;
    form.program_id = null;
    form.instrument_id = null;
    form.day_offset = null;

    campaign.value = null;
    programs.value = [];
    messages.value = [];
    instruments.value = [];
}

function enroll() {
    const campaignId = form.campaign.id;

    form.transform((data) => {
        const { student, campaign, ...rest } = data;
        return {
            studentable_id: student.id,
            studentable_type: student.type,
            campaign_id: campaign.id,
            ...rest,
        };
    }).post(route("campaignSubscriptions.store", campaignId), {
        onSuccess: (page) => {
            dialog.value.dialog = false;
            emit("success", page.props.successMessage);
            closeModal(() => reset(true));
        },
        onError: (error) => {
            console.log(error);
        },
    });
}

// Computed

/*const validTemplateCampaigns = computed(() => {
    return templateCampaigns.value.filter(
        (e) =>
            e.programs.map((e) => e.id).includes(form.student.program_id) ||
            !form.student.program_id
    );
});*/

// Watchers

watch(
    () => form.student,
    (student) => {
        if (!student) return;
        reset();
    }
);

watch(
    () => form.program_id,
    (id) => {
        const program = programs.value.find((e) => e.id == id);
        instruments.value.forEach((e) => {
            if (e.name == "Piano") return;
            e.disabled = program.title == "Accelerated Labs";
        });
    }
);

// Created

fetchCampaigns();
</script>

<template>
    <SoliForm
        ref="dialog"
        title="New Automation"
        text="Add to Automation"
        @reset="reset(true)"
        @submit="enroll()"
    >
        <template #body>
            <div class="contents">
                <div class="flex flex-col gap-4">
                    <v-select
                        v-model="form.student"
                        :items="[crmClient, ...relationships]"
                        :rules="[(v) => !!v || 'Student is required']"
                        :error-messages="errors.studentable_id"
                        item-value="id"
                        item-text="name"
                        label="Student"
                        dense
                        outlined
                        hide-details="auto"
                        return-object
                    />
                    <v-select
                        v-model="form.campaign"
                        :items="uniqueCampaignsbyNameAndCategory"
                        :loading="campaignsLoading"
                        :rules="[(v) => !!v || 'Campaign is required']"
                        dense
                        outlined
                        return-object
                        item-value="group"
                        item-text="name"
                        label="Campaign"
                        no-data-text="No campaigns available"
                        hide-details="auto"
                        class="soli-grey"
                        @input="handleCampaignSelect($event)"
                    >
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <v-list-item-title>{{
                                    item.name
                                }}</v-list-item-title>
                                <v-list-item-subtitle>{{
                                    `${item.category} | ${item.duration} days`
                                }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-select>
                    <v-select
                        v-model="form.program_id"
                        :items="programs"
                        :loading="campaignLoading"
                        item-value="id"
                        item-text="title"
                        clearable
                        label="Program"
                        dense
                        outlined
                        hide-details="auto"
                        @input="setCampaignFromProgram($event)"
                    />
                    <v-select
                        v-model="form.instrument_id"
                        :items="instruments"
                        :loading="campaignLoading"
                        item-value="id"
                        item-text="name"
                        clearable
                        label="Instrument"
                        dense
                        outlined
                        hide-details="auto"
                    />
                    <v-select
                        v-model="form.day_offset"
                        :items="messages"
                        :loading="campaignLoading"
                        :rules="[(v) => !!v || 'Starting day is required']"
                        item-value="day_offset"
                        item-text="day_offset"
                        label="Starting Day"
                        dense
                        outlined
                        hide-details="auto"
                    >
                        <template v-slot:selection="{ item }">
                            <div
                                class="v-select__selection v-select__selection--comma"
                            >
                                {{ `Day ${item.day_offset}` }}
                            </div>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <v-list-item-title>{{
                                    `Day ${item.day_offset}`
                                }}</v-list-item-title>
                                <!--<v-list-item-subtitle
                                    v-html="
                                        item.content.substring(0, 90) + '...'
                                    "
                                    class="custom-v-list-item-subtitle"
                                />-->
                            </v-list-item-content>
                        </template>
                    </v-select>
                    <!--<TipTapReader
                        v-if="false"
                        wrapper
                        :value="
                            campaign?.messages?.find(
                                (e) => e.dayOffset == form.dayOffset
                            )?.content
                        "
                    />-->
                </div>
            </div>
        </template>
    </SoliForm>
</template>
